import React, { useState, useEffect } from 'react';
import { AlertTriangle, Calendar, Phone, Clock, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '../App';

export function OverdueMaintenanceCard({ onScheduleAppointment }) {
  const [overduePatients, setOverduePatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchOverduePatients();
  }, []);

  const fetchOverduePatients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/patients/overdue-maintenance');
      setOverduePatients(response.data.patients || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Erro ao buscar pacientes atrasados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = (patient) => {
    // Fechar modal e chamar callback para abrir modal de agendamento
    setShowModal(false);
    if (onScheduleAppointment) {
      onScheduleAppointment(patient);
    }
  };

  if (loading) {
    return (
      <Card className="border-l-4 border-l-orange-500">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            Manutenção Atrasada
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">Carregando...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card 
        className={`border-l-4 ${total > 0 ? 'border-l-orange-500 cursor-pointer hover:shadow-md transition-shadow' : 'border-l-gray-300'}`}
        onClick={() => total > 0 && setShowModal(true)}
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className={`w-4 h-4 ${total > 0 ? 'text-orange-500' : 'text-gray-400'}`} />
            Manutenção Atrasada
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <div className="text-3xl font-bold text-gray-900">{total}</div>
            <p className="text-sm text-gray-600">
              {total === 0 ? 'Nenhum paciente atrasado' : 
               total === 1 ? 'paciente atrasado' : 
               'pacientes atrasados'}
            </p>
            {total > 0 && (
              <p className="text-xs text-orange-600 font-medium mt-2">
                Clique para ver a lista completa
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modal com lista de pacientes atrasados */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-orange-50 border-b border-orange-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Pacientes Atrasados na Manutenção
                  </h2>
                  <p className="text-sm text-gray-600">
                    {total} {total === 1 ? 'paciente' : 'pacientes'} não {total === 1 ? 'veio' : 'vieram'} há mais de 30 dias
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Lista de pacientes */}
            <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6">
              <div className="space-y-3">
                {overduePatients.map((patient, index) => (
                  <div
                    key={patient.patient_id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Informações do paciente */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-orange-100 text-orange-700 text-xs font-medium px-2 py-1 rounded">
                            {patient.days_overdue} dias de atraso
                          </span>
                        </div>
                        
                        <h3 className="font-semibold text-gray-900 text-lg mb-1">
                          {patient.patient_name}
                        </h3>
                        
                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
                          {patient.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              <span>{patient.phone}</span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>
                              Última consulta: {new Date(patient.last_appointment_date).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            Tipo: {patient.last_appointment_type}
                          </div>
                        </div>
                      </div>

                      {/* Botão de agendar */}
                      <Button
                        onClick={() => handleSchedule(patient)}
                        className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 shrink-0"
                      >
                        <Calendar className="w-4 h-4" />
                        Agendar Retorno
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  💡 Dica: Entre em contato com os pacientes para remarcar as manutenções
                </p>
                <Button
                  variant="outline"
                  onClick={() => setShowModal(false)}
                >
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
